import { LogSafeValue, LoggerString } from '@redngapps/shared/logging/types';

import { getLogValue } from './get-log-value';
import { isLogSafeValue } from './is-log-safe-value';

/**
 * Use this message to create a `LoggerString`.
 *
 * In a `LoggerString` every interpolation, which is not marked as save to log, will be censored.
 * To mark interpolations as save use the helper methods `logSafe` and `logSafeObject`.
 *
 * **Example**
 * ```
 * let name = "Mike";
 * let age = 28;
 *
 * logMessage`This is safe to log ${logSafe(name)} and this is not ${age}`
 * ```
 * Output: This is safe to log Mike and this is not XXX
 */
export function logMessage(strings: TemplateStringsArray, ...interpolations: (unknown | LogSafeValue)[]): LoggerString {
  const result = [];
  for (let i = 0; i < strings.length; i++) {
    result.push(strings[i]);

    if (i < interpolations.length) {
      const interpolation = getMaskedValue(interpolations[i]);
      if (typeof interpolation === 'object') {
        if (interpolation instanceof Error) {
          try {
            result.push(interpolation.stack || interpolation.toString());
          } catch (_e) {
            result.push('[unserializable error]');
          }
        } else {
          try {
            result.push(JSON.stringify(interpolation));
          } catch (_e) {
            result.push('[circular object]');
          }
        }
      } else {
        result.push(interpolation);
      }
    }
  }

  return new LoggerString(result.join(''));
}

function getMaskedValue(value: unknown | LogSafeValue): unknown {
  if (isLogSafeValue(value)) {
    return getLogValue(value);
  }

  return 'XXX';
}
